import { FC, Fragment } from 'react';
import { EdnaWrapper } from '@providers/EdnaProvider';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import styled from 'styled-components';

import { TYPOGRAPHY } from '@junglescout/styles-library';
import Icon from '../Icon';
import {
  DropdownMenuProps,
  DropdownItemProps,
  DropdownDividerProps,
} from './types';

const Label = styled(DropdownMenuPrimitive.Label)`
  ${TYPOGRAPHY.bodyXs};
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.grey500};
  padding: 0px 8px 4px 8px;
`;

const Item = styled(DropdownMenuPrimitive.Item)<{
  $danger?: boolean;
  $selected?: boolean;
}>`
  ${TYPOGRAPHY.bodyMd};
  display: inline-flex;
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.white};
  &:hover,
  &:focus {
    outline: none;
    &:not([data-disabled]) {
      background-color: ${(props) =>
        props.$danger ? props.theme.colors.red100 : props.theme.colors.grey075};
      border-radius: 2px;
    }
  }
  cursor: pointer;
  padding: 4px 8px;
  width: fill-available;
  gap: 8px;
  color: ${(props) =>
    props.$danger ? props.theme.colors.red500 : props.theme.colors.grey900};
  &[data-disabled] {
    opacity: 0.5;
    cursor: default;
  }
`;
const ItemContent = styled.div`
  display: flex;
  gap: 8px;
`;

const Content = styled(DropdownMenuPrimitive.Content)<{
  $width?: string;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1),
    0px 0px 1px 0px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  box-sizing: border-box;
  width: ${(props) =>
    props.$width || 'var(--radix-dropdown-menu-trigger-width)'};
  z-index: 999999;
  padding: 8px 4px;
  border: 1px solid ${(props) => props.theme.colors.grey100};
`;

const Divider = styled(DropdownMenuPrimitive.Separator)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey100};
  margin: 4px 0;
`;

/**
 * Use this to display a horizontal line in the menu to inidicate a
 * separation between items.
 */
const DropdownDivider = ({ className }: DropdownDividerProps) => {
  return <Divider className={className} />;
};

/**
 * Use this to display an item in the menu.
 */
const DropdownItem = ({
  disabled = false,
  danger = false,
  selected = false,
  children,
  action,
  className,
  dataId,
  testId,
  iconName,
}: DropdownItemProps) => {
  return (
    <Item
      disabled={disabled}
      $danger={danger}
      $selected={selected}
      onSelect={action}
      data-id={dataId}
      data-testid={testId}
      className={className}
      aria-selected={selected}
    >
      <ItemContent>
        {iconName && (
          <Icon
            name={iconName}
            width="16px"
            height="16px"
            color={danger ? 'red500' : 'grey700'}
            margin="2px 0 0"
          />
        )}
        {children}
      </ItemContent>
      {selected && (
        <Icon
          name="CHECK"
          width="16px"
          height="16px"
          color="grey700"
          margin="2px 0 0"
        />
      )}
    </Item>
  );
};

/**
 *
 * Displays a menu to the user with a set of actions or functions—triggered by a clickable element (button).
 *
 * The menu items should be rendered using the `DropdownMenu.Item` subcomponent.
 * Pass them in as children to `DropdownMenu`.
 *
 * You can also add a `DropdownMenu.Divider` component to display a horizontal line in the menu.
 * Note that the divider does not have any configurable props, it is just a line.
 *
 * To trigger the menu open, pass in a button to the `trigger` prop.
 *
 * You can position the dropdown menu with the `side` and `align` props. If you need to fine-tune
 * the position, you can use the `alignOffset` and `sideOffset` props as well.
 *
 * The dropdown menu has "collision avoidance" enabled. This means that if the dropdown
 * is opened somewhere on the screen where it would collide with another
 * element or with the boundaries of the page (be hidden) then it will automatically
 * reposition itself so that it remains visible.
 *
 * The dropdown menu is dislpayed in a portal to avoid overlapping issues.
 *
 * This dropdown uses the headless [dropdown menu from radix-ui](https://www.radix-ui.com/docs/primitives/components/dropdown-menu),
 * and more props can be exposed as needed.
 */

const DropdownMenu: FC<DropdownMenuProps> & {
  Item: typeof DropdownItem;
  Divider: typeof DropdownDivider;
  Label: typeof Label;
} = ({
  align = 'start',
  alignOffset,
  children,
  className,
  open,
  onOpenChange,
  side = 'bottom',
  sideOffset,
  width,
  trigger,
  modal = true,
  portal = false,
}) => {
  const ContentWrapper = portal ? DropdownMenuPrimitive.Portal : Fragment;

  return (
    <EdnaWrapper>
      <DropdownMenuPrimitive.Root
        open={open}
        onOpenChange={onOpenChange}
        modal={modal}
      >
        <DropdownMenuPrimitive.Trigger asChild>
          {trigger}
        </DropdownMenuPrimitive.Trigger>
        <ContentWrapper>
          <Content
            $width={width}
            align={align}
            side={side}
            collisionPadding={4}
            className={className}
            alignOffset={alignOffset}
            sideOffset={sideOffset}
            // loop - TODO: Make this a configurable prop
          >
            {children}
          </Content>
        </ContentWrapper>
      </DropdownMenuPrimitive.Root>
    </EdnaWrapper>
  );
};

DropdownMenu.Item = DropdownItem;
DropdownMenu.Divider = DropdownDivider;
DropdownMenu.Label = Label;

export default DropdownMenu;

export { DropdownItem as Item, DropdownDivider as Divider, Label }; // this export is used for Storybook only. Without these the subcomponents won't show props in the controls panel.
